// frontend/src/components/ManageChatters.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, setDoc, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { initializeApp } from 'firebase/app'; 
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { firebaseConfig } from '../firebase';
import axios from 'axios';

// Helper for creation
const createSecondaryUser = async (email, password) => {
    const secondaryApp = initializeApp(firebaseConfig, "Secondary");
    const secondaryAuth = getAuth(secondaryApp);
    const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    return userCredential.user;
};

export default function ManageChatters() {
    const [chatters, setChatters] = useState([]);
    const [availableModels, setAvailableModels] = useState([]);
    
    // Creation State
    const [newEmail, setNewEmail] = useState('');
    const [newPass, setNewPass] = useState('');
    
    // UI State
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('');
    const [editingChatter, setEditingChatter] = useState(null); // The user being managed

    const fetchData = async () => {
        const uSnap = await getDocs(collection(db, "users"));
        const uList = []; uSnap.forEach(d => { if (d.data().role === 'chatter') uList.push({ id: d.id, ...d.data() }) });
        setChatters(uList);

        const mSnap = await getDocs(collection(db, "models"));
        const mList = []; mSnap.forEach(d => mList.push(d.data()));
        setAvailableModels(mList);
    };

    useEffect(() => { fetchData(); }, [loading]);

    // --- ACTIONS ---
    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus('');
        try {
            const newUser = await createSecondaryUser(newEmail, newPass);
            await setDoc(doc(db, "users", newUser.uid), {
                email: newEmail, role: 'chatter', assignedModels: [], isDisabled: false, createdAt: new Date()
            });
            setStatus(`Created: ${newEmail}`);
            setNewEmail(''); setNewPass('');
        } catch (error) { setStatus(`Error: ${error.message}`); }
        setLoading(false);
    };

    const handleSaveEdit = async () => {
        setLoading(true);
        try {
            await updateDoc(doc(db, "users", editingChatter.id), {
                assignedModels: editingChatter.assignedModels || [],
                isDisabled: editingChatter.isDisabled
            });
            setEditingChatter(null);
        } catch (error) { alert(error.message); }
        setLoading(false);
    };

    const handleDelete = async () => {
        if (!window.confirm("⚠️ WARNING: This will delete the User Login AND Profile permanently.\n\nAre you sure?")) return;
        
        setLoading(true);
        try {
            // 1. Call Backend to delete the Login (Auth)
            // This uses the 'God Mode' we set up in main.py
            await axios.delete(`http://127.0.0.1:8000/api/admin/delete-user/${editingChatter.id}`);

            // 2. Delete the Profile (Firestore)
            await deleteDoc(doc(db, "users", editingChatter.id));
            
            // 3. Cleanup UI
            setChatters(prev => prev.filter(c => c.id !== editingChatter.id));
            setEditingChatter(null);
            setStatus(`✅ Deleted ${editingChatter.email} completely.`);
            
        } catch (error) {
            console.error("Delete failed:", error);
            // Even if backend fails (e.g. user already gone), try to clean up DB
            try {
                 await deleteDoc(doc(db, "users", editingChatter.id));
                 setChatters(prev => prev.filter(c => c.id !== editingChatter.id));
                 setEditingChatter(null);
                 alert("⚠️ Auth delete failed (maybe already gone?), but DB profile removed.");
            } catch (dbErr) {
                 alert("❌ Critical Error: " + error.message);
            }
        }
        setLoading(false);
    };

    // --- BUBBLE HELPERS (For Modal) ---
    const addModel = (e) => {
        const val = e.target.value;
        if (val && !editingChatter.assignedModels?.includes(val)) {
            setEditingChatter({ ...editingChatter, assignedModels: [...(editingChatter.assignedModels || []), val] });
        }
        e.target.value = "";
    };

    const removeModel = (mid) => {
        setEditingChatter({ ...editingChatter, assignedModels: editingChatter.assignedModels.filter(m => m !== mid) });
    };

    const toggleDisable = () => {
        setEditingChatter({ ...editingChatter, isDisabled: !editingChatter.isDisabled });
    };

    return (
        <div className="admin-container">
            <h2>Manage Chatters</h2>

            <div style={{ display: 'flex', gap: '20px' }}>
                {/* Creator */}
                <div style={{ flex: 1, background: '#252526', padding: '20px', borderRadius: '8px', height: 'fit-content' }}>
                    <h3>Add New Chatter</h3>
                    <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input type="email" placeholder="Email" required value={newEmail} onChange={e => setNewEmail(e.target.value)} style={inputStyle} />
                        <input type="password" placeholder="Password" required value={newPass} onChange={e => setNewPass(e.target.value)} style={inputStyle} />
                        <button disabled={loading} style={btnStyle}>Create User</button>
                    </form>
                    {status && <p style={{ color: '#00ff00', marginTop: '10px' }}>{status}</p>}
                </div>

                {/* List */}
                <div style={{ flex: 2 }}>
                    <h3>Existing Staff</h3>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {chatters.map(c => (
                            <div key={c.id} style={{ background: '#1e1e1e', padding: '15px', border: c.isDisabled ? '1px solid #a31515' : '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <strong style={{ fontSize: '1.1rem' }}>{c.email}</strong>
                                    {c.isDisabled && <span style={{ marginLeft: '10px', color: 'red', fontWeight: 'bold' }}>DISABLED</span>}
                                    <div style={{ color: '#888' }}>Access: {c.assignedModels?.length || 0} Groups</div>
                                </div>
                                <button onClick={() => setEditingChatter(c)} style={{ ...btnStyle, background: '#444' }}>Manage</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* --- MODAL: MANAGE CHATTER --- */}
            {editingChatter && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <h3>Manage: {editingChatter.email}</h3>
                            <button onClick={() => setEditingChatter(null)} style={closeBtnStyle}>✕</button>
                        </div>

                        {/* Account Status */}
                        <div style={{ marginBottom: '20px', padding: '10px', background: '#333', borderRadius: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Account Status: <strong style={{ color: editingChatter.isDisabled ? 'red' : '#00ff00' }}>{editingChatter.isDisabled ? 'DISABLED' : 'ACTIVE'}</strong></span>
                            <button onClick={toggleDisable} style={{ padding: '5px 10px', cursor: 'pointer' }}>
                                {editingChatter.isDisabled ? 'Enable' : 'Disable'}
                            </button>
                        </div>

                        {/* Assigned Models Bubbles */}
                        <label>Assigned Model Groups</label>
                        <select onChange={addModel} style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}>
                            <option value="">+ Assign Group Access...</option>
                            {availableModels.map(m => (
                                <option key={m.modelId} value={m.modelId}>{m.modelId} ({m.description})</option>
                            ))}
                        </select>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '30px', minHeight: '50px', padding: '10px', background: '#111', borderRadius: '4px' }}>
                            {(!editingChatter.assignedModels || editingChatter.assignedModels.length === 0) && <span style={{ color: '#555' }}>No access assigned.</span>}
                            {editingChatter.assignedModels?.map(mid => (
                                <div key={mid} style={{ background: '#007acc', color: 'white', padding: '5px 10px', borderRadius: '15px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    {mid}
                                    <button onClick={() => removeModel(mid)} style={{ background: 'transparent', border: 'none', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>×</button>
                                </div>
                            ))}
                        </div>

                        {/* Footer Actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <button onClick={handleDelete} style={{...btnStyle, background: '#a31515'}}>Delete User</button>
                            <button onClick={handleSaveEdit} style={{...btnStyle, background: '#2da44e', padding: '10px 20px', fontSize: '1rem'}}>Save Changes</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Reusing Styles
const inputStyle = { padding: '8px', background: '#3c3c3c', border: '1px solid #555', color: 'white' };
const btnStyle = { padding: '8px 15px', background: '#2da44e', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '3px' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContentStyle = { background: '#252526', padding: '25px', borderRadius: '8px', width: '500px', border: '1px solid #444', boxShadow: '0 4px 15px rgba(0,0,0,0.5)' };
const closeBtnStyle = { background: 'transparent', border: 'none', color: '#888', fontSize: '1.2rem', cursor: 'pointer' };